import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { App, app } from '@df/core/app';
import { ImageModule } from '@df/module-image/image.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ProductListingComponent } from '../../catalog/listing/product-listing.component';
import { ButtonStyleComponent } from '../../common/buttons/button-style/button-style.component';
import { RadioGroupItemComponent } from '../../common/form/radio/group/item/radio-group-item.component';
import { RadioGroupComponent } from '../../common/form/radio/group/radio-group.component';
import { NTextBox } from '../../common/text-box/text-box.component';
import { defaultImage } from '../../icms.config';
import { StepsBarComponent } from './steps-bar/steps-bar.component';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';

let uid = 0;

export namespace NCmsDiscoveryQuizComponent {
    export const ID = 'cms-discovery-quiz';

    export interface IData extends NICmsContentComponent.IDataWithStyles, NTextBox.IData {
        title: string;
        subtitle: string;
        buttonLabel?: string;
        image: string;
        steps: IStep[];
        stepsImage?: string;
    }

    export interface IStep {
        dependsOn?: boolean;
        dependsOnQuestion?: number;
        dependsOnAnswer?: number;
        text: string;
        attribute?: string;
        answers: IAnswer[];
    }

    export interface IAnswer {
        text: string;
        value: string;
    }

    export interface ICurrentStep {}
}

@ICmsComponent<CmsDiscoveryQuizComponent>({
    id: NCmsDiscoveryQuizComponent.ID,
    name: 'Discovery Quiz',
    tags: ['Filter', 'Quiz', 'Discovery'],
    fields: [
        {
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            required: true,
            default: 'Product Finder Quiz'
        },
        {
            key: 'subtitle',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Subtitle',
            required: true,
            default:
                'If you aren’t sure where to start, this quiz will hopefully send you in the right direction. Simply answer the following questions and we will suggest some products which will solve your problem! If you still need help give us a call or send us a message.'
        },
        {
            key: 'buttonLabel',
            type: NICmsContentField.EFieldType.text,
            title: 'Button label',
            default: 'start the quiz'
        },
        {
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            required: true,
            default: defaultImage
        },
        {
            key: 'answersLabel',
            type: NICmsContentField.EFieldType.label,
            title: 'Steps'
        },
        {
            key: 'stepsImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Image'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'steps',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Steps',
            itemName: 'Step',
            itemNameField: 'text',
            required: true,
            defaultSize: 1,
            complex: true,
            fields: [
                <NICmsContentField.IToggleField>{
                    key: 'dependsOn',
                    title: 'Depends on other step',
                    description:
                        'If checked this step will appear only if the customer selected specific answer in one of the previous steps',
                    type: NICmsContentField.EFieldType.toggle,
                    default: false
                },
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Question',
                    default: 'What’s your acoustic problem?',
                    required: true
                },
                <NICmsContentField.IChoiceField>{
                    key: 'attribute',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Attribute',
                    options: () => {
                        return app.resources.attribute
                            .getAllExisting()
                            .map(i => {
                                if (i.data?.options?.length) {
                                    return {
                                        key: i.data.code,
                                        title: i.data.label
                                    };
                                }
                                return undefined;
                            })
                            .filter(i => !!i);
                    },
                    required: true
                },
                {
                    key: 'dependsOnQuestion',
                    title: 'Depends on question',
                    description: 'Select one of the previous steps that this step depends on',
                    type: NICmsContentField.EFieldType.choice,
                    options: (data: NCmsDiscoveryQuizComponent.IStep) => {
                        const steps = (
                            app.iCmsAdminDataContentComponentService.editedComponent$.getValue() as NCmsDiscoveryQuizComponent.IData
                        )?.steps;
                        const stepId = steps?.findIndex(step => step === data);
                        return steps.slice(0, stepId).map((step, index) => ({ key: index, title: step.text }));
                    },
                    canShow: (data: NCmsDiscoveryQuizComponent.IStep) => !!data?.dependsOn
                },
                {
                    key: 'dependsOnAnswer',
                    title: 'Depends on answer',
                    type: NICmsContentField.EFieldType.choice,
                    options: (data: NCmsDiscoveryQuizComponent.IStep) => {
                        const answers = (
                            app.iCmsAdminDataContentComponentService.editedComponent$.getValue() as NCmsDiscoveryQuizComponent.IData
                        )?.steps?.[data.dependsOnQuestion!]?.answers;
                        return answers.map(answer => ({ key: answer.value, title: answer.text }));
                    },
                    canShow: (data: NCmsDiscoveryQuizComponent.IStep) => data?.dependsOnQuestion !== undefined && !!data?.dependsOn
                },
                <NICmsContentField.IRepeaterField>{
                    key: 'answers',
                    type: NICmsContentField.EFieldType.repeater,
                    title: 'Answers',
                    itemName: 'Answer',
                    itemNameField: 'text',
                    required: true,
                    defaultSize: 2,
                    minSize: 2,
                    complex: true,
                    fields: [
                        {
                            key: 'text',
                            type: NICmsContentField.EFieldType.textarea,
                            title: 'Answer',
                            default: 'I have a space with a lot of echo',
                            required: true
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'value',
                            type: NICmsContentField.EFieldType.choice,
                            options: data => {
                                const attribute = (
                                    app.iCmsAdminDataContentComponentService.editedComponent$.getValue() as NCmsDiscoveryQuizComponent.IData
                                )?.steps?.find(step => step.answers.includes(data))?.attribute;
                                return (
                                    attribute &&
                                    app.resources.attribute.get(attribute).data?.options?.map(option => ({
                                        key: option?.value,
                                        title: option?.label
                                    }))
                                );
                            },
                            title: 'Value',
                            required: true
                        }
                    ]
                }
            ]
        }
    ]
})
@Component({
    standalone: true,
    imports: [
        CommonModule,
        DfModuleImageModule,
        GridModule,
        ButtonStyleComponent,
        ICmsClientModule,
        ToggleModule,
        RadioGroupComponent,
        RadioGroupItemComponent,
        FormsModule,
        TranslationModule,
        StepsBarComponent,
        ToggleModule,
        ProductListingComponent
    ],
    selector: 'cms-discovery-quiz',
    templateUrl: './cms-discovery-quiz.html',
    styleUrl: 'cms-discovery-quiz.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsDiscoveryQuizComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'component'
    })
    component!: NCmsDiscoveryQuizComponent.IData;

    @ViewChild('form', { static: false })
    form?: NgForm;

    protected currentStep = signal(0);
    protected quizProgress = { current: 1, total: 1 };
    protected formModels = new Map<NCmsDiscoveryQuizComponent.IStep, number | undefined>();
    protected listing?: ProductListing;
    protected readonly toggleService = inject(ToggleService);
    protected readonly uid = `${NCmsDiscoveryQuizComponent.ID}-${uid++}`;
    private uiModuleConfig = inject(NCatalogUi.OPTIONS);
    private readonly app = inject(App);
    protected resultButtonBusy = signal(false);

    override ngOnInit(): void {
        super.ngOnInit();
        const dependentSteps = this.component.steps.filter(step => step.dependsOn);
        this.quizProgress.total = this.component.steps.length - dependentSteps.length;
    }

    protected goToNextStep(): void {
        if (this.currentStep() !== this.component.steps.length - 1 && this.isStepValid(this.currentStep())) {
            this.currentStep.update(currentValue => currentValue + 1);
            const step = this.component.steps[this.currentStep()];
            if (!step.dependsOn) this.quizProgress.current++;
            if (this.ifStepShouldBeSkipped(step)) {
                this.goToNextStep();
                return;
            }
            this.toggleService.toggle([this.uid, this.currentStep()]);
            this.detectChanges();
        }
    }

    protected goToPreviousStep(): void {
        if (this.currentStep() > 0) {
            this.currentStep.update(currentValue => currentValue - 1);
            const step = this.component.steps[this.currentStep()];
            const stepBeforeChange = this.component.steps[this.currentStep() + 1];
            this.formModels.delete(stepBeforeChange);
            if (!stepBeforeChange.dependsOn) this.quizProgress.current--;
            if (this.ifStepShouldBeSkipped(step)) {
                this.goToPreviousStep();
                return;
            }
            this.toggleService.toggle([this.uid, this.currentStep()]);
            this.detectChanges();
        }
    }

    protected isStepValid(index: number): boolean {
        const stepModel = this.component.steps[index];
        if (stepModel.attribute) {
            return !!this.form?.controls[stepModel.attribute]?.valid;
        }
        return false;
    }

    ifStepShouldBeSkipped(step: NCmsDiscoveryQuizComponent.IStep): boolean {
        return !!(
            step.dependsOn &&
            step.dependsOnQuestion !== undefined &&
            this.formModels.get(this.component.steps[step.dependsOnQuestion]) !== step.dependsOnAnswer
        );
    }

    protected formModelChange(step: NCmsDiscoveryQuizComponent.IStep, value: number) {
        this.formModels.set(step, value);
        this.detectChanges();
    }

    protected async seeResults(): Promise<void> {
        if (!this.resultButtonBusy()) {
            const filter = { include_in_product_finder: 1 };
            this.formModels.forEach((value, key) => {
                if (key.attribute) {
                    filter[key.attribute] = value;
                }
            });
            const products = await this.loadProducts(filter);
            this.listing = new ProductListing(
                {
                    type: 'product',
                    source: products
                },
                products,
                {
                    sortOptions: this.uiModuleConfig.listing.category.sort,
                    sizeOptions: this.uiModuleConfig.listing.category.size,
                    defaultSize: this.uiModuleConfig.listing.category.defaultSize
                }
            );
            this.detectChanges();
        }
    }

    private async loadProducts(filter: object): Promise<[] | Product<IProductDataV2>[]> {
        this.resultButtonBusy.set(true);
        this.detectChanges();
        try {
            const products = await this.app.resources.product.find({ filter: filter, verbosity: 2 });
            this.resultButtonBusy.set(false);
            return products;
        } catch {
            this.resultButtonBusy.set(false);
            return [];
        }
    }
}
